import { useReducer, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { User, LoginCredentials, UserInfo, AuthState, LoginResponse } from '../types';
import { AuthService } from '../services/authService';
import { setSessionExpiredHandler, setImpersonationExpiredHandler } from '../services/api';
import { AuthContext, AuthContextType } from './AuthContextDef';
import { reportPdfFlagsFromUserInfo } from '../utils/reportPdfFlags';
import {
  ROLE_SUPER_ADMIN,
  ROLE_EXTERNAL,
  ROLE_PARTNER,
  ROLE_PARTNER_PRODUCT_MANAGER,
  ROLE_PARTNER_API_KEY_MANAGER,
  ROLE_PARTNER_ADMIN,
  ADMIN_ROLES,
  MENU_ID_PARTNER_PRODUCTS,
  MENU_ID_PARTNER_TEAM,
  MENU_ID_PARTNER_API_KEYS,
} from '../constants/roles';

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'TWOFA_VERIFY_FAILURE'; payload: string }
  | { type: 'LOGIN_REQUIRES_2FA'; payload: LoginCredentials }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'MARK_ONBOARDING_DISMISSED' }
  | { type: 'IMPERSONATE_START'; payload: { user: User; impersonatorEmail: string } }
  | { type: 'IMPERSONATE_STOP'; payload: User }
  | { type: 'USER_REFRESHED'; payload: User }
  | { type: 'INITIALIZE'; payload: User | null };

interface ExtendedAuthState extends AuthState {
  pendingCredentials: LoginCredentials | null;
  impersonating: boolean;
  impersonator: { email: string } | null;
}

const initialState: ExtendedAuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  pendingCredentials: null,
  impersonating: false,
  impersonator: null,
};

// Pure UserInfo → User mapper. Module-scoped (not recreated per render) so hooks that reference it have a
// stable identity.
const convertUserInfoToUser = (userInfo: UserInfo): User => ({
  id: userInfo.id,
  email: userInfo.email,
  name: `${userInfo.firstName} ${userInfo.lastName}`,
  firstName: userInfo.firstName,
  lastName: userInfo.lastName,
  phone: userInfo.phone ?? null,
  roles: userInfo.roles,
  primaryRole: userInfo.roles[0] || 'User',
  organization: 'DRIS System',
  createdAt: new Date(),
  lastLogin: new Date(userInfo.lastLogin),
  allowedMenuIds: userInfo.allowedMenuIds || [],
  hasDataAccess: userInfo.hasDataAccess,
  onboardingDismissed: userInfo.onboardingDismissed,
  billingEnabled: userInfo.billingEnabled ?? false,
  smartSamplingExternalEnabled: userInfo.smartSamplingExternalEnabled ?? false,
  soilMicroNormsCalculatorEnabled: userInfo.soilMicroNormsCalculatorEnabled ?? false,
  // DRI-95: absent (older API image) = on, so prod keeps its PDF buttons until the switch is sent.
  ...reportPdfFlagsFromUserInfo(userInfo),
});

function authReducer(state: ExtendedAuthState, action: AuthAction): ExtendedAuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        error: action.payload,
        pendingCredentials: null,
      };
    case 'TWOFA_VERIFY_FAILURE':
      // A wrong/expired 2FA code is a RETRYABLE step failure, NOT a dead login. Unlike LOGIN_FAILURE
      // we deliberately KEEP pendingCredentials so the user can re-enter the code in the same dialog.
      // Clearing them (the old behaviour, via LOGIN_FAILURE) stranded the login: every subsequent
      // attempt hit the verify2FA guard and threw "No pending login credentials" until a full reload.
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        error: action.payload,
      };
    case 'LOGIN_REQUIRES_2FA':
      return {
        ...state,
        isLoading: false,
        isInitialized: true,
        pendingCredentials: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'MARK_ONBOARDING_DISMISSED':
      // In-memory patch after the dismiss API succeeds, so the forced-onboarding landing gate stops
      // firing within the session (prevents a redirect loop back into onboarding).
      return state.user
        ? { ...state, user: { ...state.user, onboardingDismissed: true } }
        : state;
    case 'IMPERSONATE_START':
      // state.user becomes the TARGET, so every role-gated read (isExternal, canAccessMenu, …) reflects
      // the impersonated user. The banner is driven by `impersonating`.
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isInitialized: true,
        impersonating: true,
        impersonator: { email: action.payload.impersonatorEmail },
        error: null,
      };
    case 'IMPERSONATE_STOP':
      // Restore the admin as state.user and clear the impersonation flags.
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isInitialized: true,
        impersonating: false,
        impersonator: null,
        error: null,
      };
    case 'USER_REFRESHED':
      // Replace the in-memory user after a self-service profile edit; every other flag is untouched.
      return { ...state, user: action.payload };
    case 'INITIALIZE':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: action.payload !== null,
        isLoading: false,
        isInitialized: true,
      };
    default:
      return state;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, {
    ...initialState,
    isLoading: true, // Start with loading true for initialization
  });

  // Role helper functions
  const hasRole = useCallback((role: string): boolean => {
    return state.user?.roles.includes(role) || false;
  }, [state.user]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    return roles.some(role => state.user?.roles.includes(role)) || false;
  }, [state.user]);

  const hasAllRoles = useCallback((roles: string[]): boolean => {
    if (!state.user) return false;
    return roles.every(role => state.user?.roles.includes(role));
  }, [state.user]);

  // Convenience properties for common role checks
  const isAdmin = useMemo((): boolean => {
    return state.user?.roles.some(r => ADMIN_ROLES.includes(r)) || false;
  }, [state.user]);

  const isSuperAdmin = useMemo((): boolean => {
    return state.user?.roles.includes(ROLE_SUPER_ADMIN) || false;
  }, [state.user]);

  // External (Agent Portal) user — row-level access only, never sees DRIS indices/norms.
  const isExternal = useMemo((): boolean => {
    return state.user?.roles.includes(ROLE_EXTERNAL) || false;
  }, [state.user]);

  // Partner (Partner Portal) user — submits + reads own/granted results via /api/partner/v1.
  const isPartner = useMemo((): boolean => {
    return state.user?.roles.includes(ROLE_PARTNER) || false;
  }, [state.user]);

  // Partner product manager — an add-on capability role that unlocks the per-partner Products
  // surface. Distinct from `isPartner`: a plain Partner is false here and must NOT reach Products.
  const isPartnerProductManager = useMemo((): boolean => {
    return state.user?.roles.includes(ROLE_PARTNER_PRODUCT_MANAGER) || false;
  }, [state.user]);

  // Partner API-key manager — add-on capability that unlocks PAT minting (the API Keys page). A
  // plain Partner without it must NOT reach the API Keys surface.
  const isPartnerApiKeyManager = useMemo((): boolean => {
    return state.user?.roles.includes(ROLE_PARTNER_API_KEY_MANAGER) || false;
  }, [state.user]);

  // Partner admin — keystone add-on that unlocks team management (the Team page). A plain Partner
  // is false here and must NOT reach it.
  const isPartnerAdmin = useMemo((): boolean => {
    return state.user?.roles.includes(ROLE_PARTNER_ADMIN) || false;
  }, [state.user]);

  // Menu access control
  const canAccessMenu = useCallback((menuId: string): boolean => {
    if (!state.user) return false;
    // SuperAdmin/Admin always has full access
    if (isSuperAdmin || isAdmin) return true;
    // External free-tier floor: the intake surface (Submit Samples + My Submissions) AND the
    // batches/reports portal are always ROUTE-reachable, regardless of seeded menu permissions.
    // The batches/reports portal ("My Reports") is only HIDDEN FROM THE SIDEBAR for zero-grant
    // accounts (see Layout.filterMenuItems) — the route itself stays reachable so a confined
    // account landing on /portal never hits AccessDenied (the dashboard shows its pending-access
    // empty state / redirects free-tier users to intake). AppLayout redirects External away from
    // everything else.
    if (
      isExternal &&
      (menuId === 'customer-home' ||
        menuId === 'analyse-results' ||
        menuId === 'intake-submit' ||
        menuId === 'my-submissions' ||
        menuId === 'file-upload' ||
        menuId === 'my-farmers' ||
        menuId === 'onboarding' ||
        menuId === 'agent-portal' ||
        menuId === 'agent-portal-workspace' ||
        // New External IA (plan §4): Samples / Reports / Farms / Profile + the route-only Welcome
        // and Buy pages. On the floor so the switch to the new nav needs no menu-permission reseed.
        menuId === 'portal-samples' ||
        menuId === 'portal-reports' ||
        menuId === 'portal-farms' ||
        menuId === 'portal-profile' ||
        menuId === 'portal-welcome' ||
        menuId === 'portal-buy' ||
        menuId === 'portal-buy-return' ||
        // SS External self-service (Phase 4a). On the floor so the route is reachable; the sidebar
        // entry + Home card are additionally gated on `smartSamplingExternalEnabled`
        // (Layout.filterMenuItems). The façade is the server-side boundary.
        menuId === 'portal-smart-sampling')
    ) {
      return true;
    }
    // Partner users may always reach the partner portal surface (all 'partner-*' menus) — EXCEPT the
    // capability-gated screens below, which require their specific add-on role rather than the
    // universal partner floor. These exceptions MUST precede the floor check. A plain Partner must
    // not reach them; only an account that ALSO holds the matching capability role may.
    if (menuId === MENU_ID_PARTNER_PRODUCTS) return isPartnerProductManager;
    if (menuId === MENU_ID_PARTNER_TEAM) return isPartnerAdmin;
    if (menuId === MENU_ID_PARTNER_API_KEYS) return isPartnerApiKeyManager;
    if (isPartner && menuId.startsWith('partner-')) return true;
    // Boundary roles get their guaranteed portal floor (granted above) plus whatever an admin
    // granted via /admin/menu-permissions (allowedMenuIds) — but never the backward-compat
    // fallback below, which would advertise the whole internal menu tree in their sidebar
    // whenever allowedMenuIds is empty (the server-side boundary still blocks the data, but the
    // partner/external sidebar shouldn't promise screens that 403). Mirrors Layout.filterMenuItems.
    if (isExternal || isPartner || isPartnerProductManager || isPartnerApiKeyManager || isPartnerAdmin) return state.user.allowedMenuIds?.includes(menuId) ?? false;
    // If no allowedMenuIds loaded yet, fall back to allowing access (backward compat)
    if (!state.user.allowedMenuIds || state.user.allowedMenuIds.length === 0) return true;
    return state.user.allowedMenuIds.includes(menuId);
  }, [state.user, isSuperAdmin, isAdmin, isExternal, isPartner, isPartnerProductManager, isPartnerApiKeyManager, isPartnerAdmin]);

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Fast path: an in-memory access token that is still valid (e.g. a StrictMode re-mount within
        // the same page load). The access token is memory-only (PR3), so on a genuine fresh page load
        // there is none and we fall through to the silent cookie refresh below.
        if (AuthService.isAuthenticated()) {
          const userInfo = await AuthService.getCurrentUser();
          const user = convertUserInfoToUser(userInfo);
          dispatch({ type: 'INITIALIZE', payload: user });
          return;
        }

        // Silent refresh on load: the httpOnly refresh cookie supplies the credential (no body/token is
        // read here); a one-time legacy localStorage token is migrated by refreshAccessToken if present.
        // Use the reduced INITIAL budget (a single attempt) so a passing outage on load cannot stack
        // multiple request timeouts behind the splash — a transient outcome still keeps the session.
        const outcome = await AuthService.refreshAccessToken(AuthService.INITIAL_REFRESH_MAX_ATTEMPTS);
        if (outcome === 'refreshed') {
          const userInfo = await AuthService.getCurrentUser();
          const user = convertUserInfoToUser(userInfo);
          dispatch({ type: 'INITIALIZE', payload: user });
        } else {
          // 'transient' or 'invalid': show the unauthenticated app for now. On a TRANSIENT failure
          // refreshAccessToken deliberately KEEPS the session (it does not clearAuthData), so a later
          // request can silently recover it rather than forcing a re-login on a passing outage. On
          // 'invalid' auth was already cleared.
          dispatch({ type: 'INITIALIZE', payload: null });
        }
      } catch {
        AuthService.clearAuthData();
        dispatch({ type: 'INITIALIZE', payload: null });
      }
    };

    initializeAuth();
  }, []);

  // Register session expired handler so api.ts can trigger logout via React state
  useEffect(() => {
    setSessionExpiredHandler(() => {
      dispatch({ type: 'LOGOUT' });
    });
    return () => setSessionExpiredHandler(() => {});
  }, []);

  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await AuthService.login(credentials);
      
      if ('requires2FA' in response && response.requires2FA) {
        dispatch({ type: 'LOGIN_REQUIRES_2FA', payload: credentials });
        return { requires2FA: true };
      }
      
      const loginResponse = response as LoginResponse;
      const user = convertUserInfoToUser(loginResponse.user);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      dispatch({ 
        type: 'LOGIN_FAILURE', 
        payload: error instanceof Error ? error.message : 'Login failed' 
      });
      throw error;
    }
  };

  // Same state transition as a successful login(), for a session the server minted on email
  // verification (plan P13). Synchronous: the cookie is already set and the payload is in hand.
  const signInWithSession = useCallback((loginResponse: LoginResponse) => {
    AuthService.adoptSession(loginResponse);
    dispatch({ type: 'LOGIN_SUCCESS', payload: convertUserInfoToUser(loginResponse.user) });
  }, []);

  const verify2FA = async (code: string) => {
    if (!state.pendingCredentials) {
      throw new Error('No pending login credentials');
    }

    dispatch({ type: 'LOGIN_START' });
    
    try {
      const loginResponse = await AuthService.verify2FA(
        state.pendingCredentials.email,
        state.pendingCredentials.password,
        code
      );
      const user = convertUserInfoToUser(loginResponse.user);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      // TWOFA_VERIFY_FAILURE (not LOGIN_FAILURE): surface the error but KEEP pendingCredentials so the
      // user can retry the code in the same dialog rather than being stranded on the next attempt.
      dispatch({
        type: 'TWOFA_VERIFY_FAILURE',
        payload: error instanceof Error ? error.message : '2FA verification failed'
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch {
      // Continue with local logout even if server call fails
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Optimistically mark onboarding dismissed in-memory (call after the dismiss API succeeds). The
  // durable flag lives server-side and arrives on the next /me; this keeps the session's landing gate
  // from re-forcing onboarding immediately after the user skips/finishes.
  const markOnboardingDismissed = useCallback(() => {
    dispatch({ type: 'MARK_ONBOARDING_DISMISSED' });
  }, []);

  // Re-read /me and replace state.user (Profile › Details after a save). Accepts an already-fetched
  // UserInfo (the PUT /me/profile response IS a refreshed /me) so a save costs one request, not two.
  const refreshUser = useCallback(async (fresh?: UserInfo) => {
    const me = fresh ?? (await AuthService.getCurrentUser());
    dispatch({ type: 'USER_REFRESHED', payload: convertUserInfoToUser(me) });
  }, []);

  // Start impersonating a user (SuperAdmin only). AuthService swaps the in-memory token to the target's;
  // re-fetching /me makes state.user the TARGET (isImpersonating true), so every role gate reflects them.
  const startImpersonation = useCallback(async (userId: number, reason: string) => {
    const started = await AuthService.startImpersonation(userId, reason);
    const me = await AuthService.getCurrentUser();
    dispatch({
      type: 'IMPERSONATE_START',
      payload: {
        user: convertUserInfoToUser(me),
        impersonatorEmail: me.impersonatorEmail ?? started.impersonatorEmail,
      },
    });
  }, []);

  // Stop impersonating and restore the admin session. AuthService restores the admin token (refreshing off
  // the admin cookie if it expired); re-fetching /me makes state.user the admin again. Used by the Exit
  // button AND (via the registered handler below) when the impersonation token expires mid-session.
  const stopImpersonation = useCallback(async () => {
    await AuthService.stopImpersonation();
    const me = await AuthService.getCurrentUser();
    dispatch({ type: 'IMPERSONATE_STOP', payload: convertUserInfoToUser(me) });
  }, []);

  // Register the impersonation-expired handler so api.ts can fail safe on a 401 WHILE impersonating: end
  // impersonation and restore the admin session instead of refreshing (which would clobber identity, C1).
  useEffect(() => {
    setImpersonationExpiredHandler(() => {
      // Fire-and-forget: restore the admin session in the background; errors fall back to a clean logout.
      stopImpersonation().catch(() => {
        AuthService.clearAuthData();
        dispatch({ type: 'LOGOUT' });
      });
    });
    return () => setImpersonationExpiredHandler(() => {});
  }, [stopImpersonation]);

  const value: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    error: state.error,
    pendingCredentials: state.pendingCredentials,
    login,
    verify2FA,
    signInWithSession,
    logout,
    clearError,
    markOnboardingDismissed,
    refreshUser,
    // Impersonation
    impersonating: state.impersonating,
    impersonator: state.impersonator,
    startImpersonation,
    stopImpersonation,
    // Role helper functions
    hasRole,
    hasAnyRole,
    hasAllRoles,
    // Convenience properties
    isAdmin,
    isSuperAdmin,
    isExternal,
    isPartner,
    isPartnerProductManager,
    isPartnerApiKeyManager,
    isPartnerAdmin,
    // Menu access control
    canAccessMenu,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

